import React, { useState, useRef } from 'react';
import { SaveIcon, XIcon, FileTextIcon, AlertTriangleIcon, CheckIcon, UploadIcon } from 'lucide-react';
import { ExchangeRate, ExchangeRateType, EntityScope } from '../CurrencyExchangeView';
interface ExchangeRateUploadProps {
  onImport: (rates: ExchangeRate[]) => void;
  onCancel: () => void;
  locale: string;
}
export function ExchangeRateUpload({
  onImport,
  onCancel,
  locale
}: ExchangeRateUploadProps) {
  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<any[]>([]);
  const [mappings, setMappings] = useState({
    fromCurrency: '',
    toCurrency: '',
    rate: '',
    type: '',
    scope: '',
    isActive: ''
  });
  const [availableColumns, setAvailableColumns] = useState<string[]>([]);
  const [previewData, setPreviewData] = useState<ExchangeRate[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [step, setStep] = useState<'upload' | 'map' | 'preview'>('upload');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    setFile(selectedFile);
    // Check file type
    const fileType = selectedFile.type;
    if (fileType !== 'text/csv' && fileType !== 'application/vnd.ms-excel' && !fileType.includes('spreadsheetml')) {
      setErrors(['Unsupported file format. Please upload a CSV or Excel file.']);
      return;
    }
    // Store document type information with the file
    const fileWithType = {
      file: selectedFile,
      documentType: 'Exchange Rate Data',
      name: selectedFile.name,
      size: selectedFile.size,
      uploadDate: new Date().toISOString()
    };
    // For this demo, we'll just parse CSV files
    // In a real application, you would use a library like xlsx for Excel files
    if (fileType === 'text/csv') {
      const reader = new FileReader();
      reader.onload = event => {
        try {
          const csvData = event.target?.result as string;
          const lines = csvData.split('\n');
          const headers = lines[0].split(',').map(header => header.trim());
          setAvailableColumns(headers);
          const data = [];
          for (let i = 1; i < lines.length; i++) {
            if (lines[i].trim() === '') continue;
            const values = lines[i].split(',').map(value => value.trim());
            const row = {};
            headers.forEach((header, index) => {
              row[header] = values[index];
            });
            data.push(row);
          }
          setParsedData(data);
          setStep('map');
          setErrors([]);
        } catch (error) {
          setErrors(['Failed to parse CSV file. Please check the format.']);
        }
      };
      reader.readAsText(selectedFile);
    } else {
      // For demo purposes, we'll show a message for Excel files
      setErrors(['Excel file parsing is not implemented in this demo. Please use CSV format.']);
    }
  };
  const handleMappingChange = (field: string, column: string) => {
    setMappings({
      ...mappings,
      [field]: column
    });
  };
  const validateAndPreview = () => {
    // Validate required mappings
    if (!mappings.fromCurrency || !mappings.toCurrency || !mappings.rate) {
      setErrors(['From Currency, To Currency, and Rate mappings are required.']);
      return;
    }
    try {
      // Convert parsed data to ExchangeRate format
      const convertedData: ExchangeRate[] = parsedData.map((row, index) => {
        const rateValue = parseFloat(row[mappings.rate]);
        if (isNaN(rateValue)) {
          throw new Error(`Invalid rate value at row ${index + 1}: ${row[mappings.rate]}`);
        }
        return {
          id: `import-${Date.now()}-${index}`,
          fromCurrency: row[mappings.fromCurrency],
          toCurrency: row[mappings.toCurrency],
          rate: rateValue,
          inverseRate: 1 / rateValue,
          type: mappings.type && row[mappings.type] ? row[mappings.type] as ExchangeRateType : 'Manual',
          scope: mappings.scope && row[mappings.scope] ? row[mappings.scope] as EntityScope : 'Global',
          isActive: mappings.isActive ? row[mappings.isActive] === 'true' || row[mappings.isActive] === 'TRUE' || row[mappings.isActive] === '1' : true,
          lastUpdated: new Date().toISOString(),
          updatedBy: 'Import'
        };
      });
      // Validate data
      const validationErrors: string[] = [];
      convertedData.forEach((rate, index) => {
        if (!rate.fromCurrency) {
          validationErrors.push(`Row ${index + 1}: Missing From Currency`);
        }
        if (!rate.toCurrency) {
          validationErrors.push(`Row ${index + 1}: Missing To Currency`);
        }
        if (isNaN(rate.rate) || rate.rate <= 0) {
          validationErrors.push(`Row ${index + 1}: Invalid Rate value`);
        }
      });
      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        return;
      }
      setPreviewData(convertedData);
      setStep('preview');
      setErrors([]);
    } catch (error) {
      setErrors([error.message || 'Failed to process data. Please check your mappings.']);
    }
  };
  const handleImport = () => {
    onImport(previewData);
  };
  return <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-9809">
      <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-9810">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-9811">
          Import Exchange Rates
        </h3>
        <button onClick={onCancel} className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9812">
          <XIcon className="h-5 w-5" data-id="element-9813" />
        </button>
      </div>
      <div className="p-6 space-y-6" data-id="element-9814">
        {/* Step indicator */}
        <div className="flex items-center justify-center mb-6" data-id="element-9815">
          <div className={`flex items-center ${step === 'upload' ? 'text-indigo-600' : 'text-gray-500'}`} data-id="element-9816">
            <div className={`flex items-center justify-center h-8 w-8 rounded-full ${step === 'upload' ? 'bg-indigo-100' : 'bg-gray-100'}`} data-id="element-9817">
              <UploadIcon className="h-4 w-4" data-id="element-9818" />
            </div>
            <span className="ml-2 text-sm font-medium" data-id="element-9819">Upload File</span>
          </div>
          <div className="h-1 w-8 bg-gray-200 mx-2" data-id="element-9820"></div>
          <div className={`flex items-center ${step === 'map' ? 'text-indigo-600' : 'text-gray-500'}`} data-id="element-9821">
            <div className={`flex items-center justify-center h-8 w-8 rounded-full ${step === 'map' ? 'bg-indigo-100' : 'bg-gray-100'}`} data-id="element-9822">
              <FileTextIcon className="h-4 w-4" data-id="element-9823" />
            </div>
            <span className="ml-2 text-sm font-medium" data-id="element-9824">Map Fields</span>
          </div>
          <div className="h-1 w-8 bg-gray-200 mx-2" data-id="element-9825"></div>
          <div className={`flex items-center ${step === 'preview' ? 'text-indigo-600' : 'text-gray-500'}`} data-id="element-9826">
            <div className={`flex items-center justify-center h-8 w-8 rounded-full ${step === 'preview' ? 'bg-indigo-100' : 'bg-gray-100'}`} data-id="element-9827">
              <CheckIcon className="h-4 w-4" data-id="element-9828" />
            </div>
            <span className="ml-2 text-sm font-medium" data-id="element-9829">Preview & Import</span>
          </div>
        </div>
        {/* Error messages */}
        {errors.length > 0 && <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4" data-id="element-9830">
            <div className="flex" data-id="element-9831">
              <div className="flex-shrink-0" data-id="element-9832">
                <AlertTriangleIcon className="h-5 w-5 text-red-400" data-id="element-9833" />
              </div>
              <div className="ml-3" data-id="element-9834">
                <h3 className="text-sm font-medium text-red-800" data-id="element-9835">
                  There{' '}
                  {errors.length === 1 ? 'is an issue' : `are ${errors.length} issues`}{' '}
                  with your data
                </h3>
                <div className="mt-2 text-sm text-red-700" data-id="element-9836">
                  <ul className="list-disc pl-5 space-y-1" data-id="element-9837">
                    {errors.map((error, index) => <li key={index} data-id="element-9838">{error}</li>)}
                  </ul>
                </div>
              </div>
            </div>
          </div>}
        {/* Upload step */}
        {step === 'upload' && <div data-id="element-9839">
            <div className="flex items-center justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md" data-id="element-9840">
              <div className="space-y-1 text-center" data-id="element-9841">
                <FileTextIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-9842" />
                <div className="flex text-sm text-gray-600" data-id="element-9843">
                  <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500" data-id="element-9844">
                    <span data-id="element-9845">Upload a file</span>
                    <input id="file-upload" name="file-upload" type="file" className="sr-only" ref={fileInputRef} onChange={handleFileUpload} accept=".csv,.xls,.xlsx" data-id="element-9846" />
                  </label>
                  <p className="pl-1" data-id="element-9847">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500" data-id="element-9848">
                  CSV or Excel file with exchange rate data
                </p>
              </div>
            </div>
            <div className="mt-6" data-id="element-9849">
              <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="element-9850">
                Expected format:
              </h4>
              <div className="bg-gray-50 p-4 rounded-md overflow-x-auto" data-id="element-9851">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-9852">
                  <thead data-id="element-9853">
                    <tr data-id="element-9854">
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9855">
                        From Currency
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9856">
                        To Currency
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9857">
                        Rate
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9858">
                        Type (Optional)
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9859">
                        Scope (Optional)
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9860">
                        Is Active (Optional)
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-9861">
                    <tr data-id="element-9862">
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-9863">
                        USD
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-9864">
                        EUR
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-9865">
                        0.92
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-9866">
                        Manual
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-9867">
                        Global
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-9868">
                        TRUE
                      </td>
                    </tr>
                    <tr data-id="element-9869">
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-9870">
                        EUR
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-9871">
                        GBP
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-9872">
                        0.87
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-9873">
                        Manual
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-9874">
                        Global
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-9875">
                        TRUE
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-xs text-gray-500" data-id="element-9876">
                * Header names in your file can be different - you'll map them
                in the next step
              </p>
            </div>
          </div>}
        {/* Mapping step */}
        {step === 'map' && <div data-id="element-9877">
            <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-9878">
              Map your file columns to exchange rate fields
            </h4>
            <div className="space-y-4" data-id="element-9879">
              <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-2" data-id="element-9880">
                <div data-id="element-9881">
                  <label htmlFor="fromCurrency" className="block text-sm font-medium text-gray-700" data-id="element-9882">
                    From Currency *
                  </label>
                  <select id="fromCurrency" value={mappings.fromCurrency} onChange={e => handleMappingChange('fromCurrency', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" required data-id="element-9883">
                    <option value="" data-id="element-9884">Select column</option>
                    {availableColumns.map(column => <option key={column} value={column} data-id="element-9885">
                        {column}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-9886">
                  <label htmlFor="toCurrency" className="block text-sm font-medium text-gray-700" data-id="element-9887">
                    To Currency *
                  </label>
                  <select id="toCurrency" value={mappings.toCurrency} onChange={e => handleMappingChange('toCurrency', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" required data-id="element-9888">
                    <option value="" data-id="element-9889">Select column</option>
                    {availableColumns.map(column => <option key={column} value={column} data-id="element-9890">
                        {column}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-9891">
                  <label htmlFor="rate" className="block text-sm font-medium text-gray-700" data-id="element-9892">
                    Rate *
                  </label>
                  <select id="rate" value={mappings.rate} onChange={e => handleMappingChange('rate', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" required data-id="element-9893">
                    <option value="" data-id="element-9894">Select column</option>
                    {availableColumns.map(column => <option key={column} value={column} data-id="element-9895">
                        {column}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-9896">
                  <label htmlFor="type" className="block text-sm font-medium text-gray-700" data-id="element-9897">
                    Type (Optional)
                  </label>
                  <select id="type" value={mappings.type} onChange={e => handleMappingChange('type', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-9898">
                    <option value="" data-id="element-9899">Select column</option>
                    {availableColumns.map(column => <option key={column} value={column} data-id="element-9900">
                        {column}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-9901">
                  <label htmlFor="scope" className="block text-sm font-medium text-gray-700" data-id="element-9902">
                    Scope (Optional)
                  </label>
                  <select id="scope" value={mappings.scope} onChange={e => handleMappingChange('scope', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-9903">
                    <option value="" data-id="element-9904">Select column</option>
                    {availableColumns.map(column => <option key={column} value={column} data-id="element-9905">
                        {column}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-9906">
                  <label htmlFor="isActive" className="block text-sm font-medium text-gray-700" data-id="element-9907">
                    Is Active (Optional)
                  </label>
                  <select id="isActive" value={mappings.isActive} onChange={e => handleMappingChange('isActive', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-9908">
                    <option value="" data-id="element-9909">Select column</option>
                    {availableColumns.map(column => <option key={column} value={column} data-id="element-9910">
                        {column}
                      </option>)}
                  </select>
                </div>
              </div>
              {parsedData.length > 0 && <div className="mt-6" data-id="element-9911">
                  <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="element-9912">
                    Data preview (first 5 rows):
                  </h4>
                  <div className="bg-gray-50 p-4 rounded-md overflow-x-auto" data-id="element-9913">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-9914">
                      <thead data-id="element-9915">
                        <tr data-id="element-9916">
                          {availableColumns.map(column => <th key={column} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9917">
                              {column}
                            </th>)}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-9918">
                        {parsedData.slice(0, 5).map((row, rowIndex) => <tr key={rowIndex} data-id="element-9919">
                            {availableColumns.map(column => <td key={column} className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-9920">
                                {row[column]}
                              </td>)}
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-2 text-xs text-gray-500" data-id="element-9921">
                    Showing {Math.min(5, parsedData.length)} of{' '}
                    {parsedData.length} rows
                  </p>
                </div>}
            </div>
          </div>}
        {/* Preview step */}
        {step === 'preview' && <div data-id="element-9922">
            <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-9923">
              Review and confirm import ({previewData.length} exchange rates)
            </h4>
            <div className="bg-gray-50 p-4 rounded-md overflow-x-auto" data-id="element-9924">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-9925">
                <thead className="bg-gray-50" data-id="element-9926">
                  <tr data-id="element-9927">
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9928">
                      From
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9929">
                      To
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9930">
                      Rate
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9931">
                      Inverse
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9932">
                      Type
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9933">
                      Scope
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9934">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-9935">
                  {previewData.slice(0, 10).map((rate, index) => <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-id="element-9936">
                      <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-9937">
                        {rate.fromCurrency}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-9938">
                        {rate.toCurrency}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-9939">
                        {rate.rate.toFixed(4)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-9940">
                        {rate.inverseRate.toFixed(4)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-9941">
                        {rate.type}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-9942">
                        {rate.scope}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-9943">
                        {rate.isActive ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-9944">
                            Active
                          </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-9945">
                            Inactive
                          </span>}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
            {previewData.length > 10 && <p className="mt-2 text-xs text-gray-500" data-id="element-9946">
                Showing 10 of {previewData.length} exchange rates
              </p>}
            <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-400 p-4" data-id="element-9947">
              <div className="flex" data-id="element-9948">
                <div className="flex-shrink-0" data-id="element-9949">
                  <AlertTriangleIcon className="h-5 w-5 text-yellow-400" data-id="element-9950" />
                </div>
                <div className="ml-3" data-id="element-9951">
                  <p className="text-sm text-yellow-700" data-id="element-9952">
                    Importing these rates will update any existing rates with
                    the same currency pairs. This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>
          </div>}
        {/* Action buttons */}
        <div className="pt-5 border-t border-gray-200" data-id="element-9953">
          <div className="flex justify-end space-x-3" data-id="element-9954">
            {step !== 'upload' && <button type="button" onClick={() => setStep(step === 'preview' ? 'map' : 'upload')} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9955">
                Back
              </button>}
            <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9956">
              Cancel
            </button>
            {step === 'upload' && file && <button type="button" onClick={() => fileInputRef.current?.click()} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9957">
                Change File
              </button>}
            {step === 'map' && <button type="button" onClick={validateAndPreview} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9958">
                <CheckIcon className="h-4 w-4 mr-2" data-id="element-9959" />
                Continue
              </button>}
            {step === 'preview' && <button type="button" onClick={handleImport} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9960">
                <SaveIcon className="h-4 w-4 mr-2" data-id="element-9961" />
                Import Rates
              </button>}
          </div>
        </div>
      </div>
    </div>;
}